import React from 'react';
import { ActiveSession } from '../../types/audit';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/Feedback';
import { Monitor, Smartphone, Globe, XCircle } from 'lucide-react';

interface SessionManagerProps {
  sessions: ActiveSession[];
  onRevoke: (id: string) => void;
  isRevoking?: boolean;
}

const deviceIcon = (ua: string) => {
  if (/Mobile|Android|iPhone/i.test(ua)) return <Smartphone className="w-4 h-4" />;
  if (/Chrome|Safari|Firefox/i.test(ua)) return <Monitor className="w-4 h-4" />;
  return <Globe className="w-4 h-4" />;
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const SessionManager: React.FC<SessionManagerProps> = ({ sessions, onRevoke }) => {
  if (sessions.length === 0) {
    return <EmptyState message="No active sessions" description="All users are currently signed out." />;
  }

  return (
    <div className="space-y-2">
      {sessions.map(session => (
        <div key={session.session_id} className="flex items-center gap-3 p-3 bg-slate-800/40 border border-slate-700/50 rounded-lg">
          <div className="p-2 rounded-lg bg-slate-700/50 text-slate-300">
            {deviceIcon(session.user_agent)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-200 truncate">{session.user_name}</span>
              {session.is_current && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Current</span>
              )}
            </div>
            <div className="text-[10px] text-slate-500 truncate">
              {session.ip_address} · active {formatRelative(session.last_active)}
            </div>
          </div>
          {!session.is_current && (
            <Button
              variant="ghost"
              size="sm"
              className="text-rose-400 hover:bg-rose-500/10"
              onClick={() => onRevoke(session.session_id)}
              icon={<XCircle className="w-3.5 h-3.5" />}
            >
              Revoke
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};
